// Sons do escritório (B29 / 5.2): tudo sintetizado no navegador com Web Audio, sem arquivos.
// Desligados por padrão. Cada categoria tem o seu liga/desliga e há um volume geral; a escolha
// fica no localStorage. Com a aba oculta só o sino toca. As regras de quando tocar (pode, cliques
// por tique, quem chegou ou saiu) são funções puras, testadas em tests/sound.test.ts.

import type { Agent } from '../core/types.js';
import { type Chave, t } from './i18n.js';

export type Categoria = 'sino' | 'teclado' | 'elevador' | 'concluida' | 'rodas';
export const CATEGORIAS: readonly Categoria[] = ['sino', 'teclado', 'elevador', 'concluida', 'rodas'];
/** As rodas sociais (a vida social chama tocaRoda). */
export type Roda = 'pingue-pongue' | 'fliperama' | 'jokenpo';

export interface PreferenciasSons {
  /** Volume geral, 0 a 100. Com 0 nada toca. */
  volume: number;
  ligados: Record<Categoria, boolean>;
}

const CHAVE = 'agzos.sons';
/** O botão Som de antes (B29-M5) guardava um liga/desliga só; ele vira o sino. */
const CHAVE_LEGADA = 'agzos.avisos.som';
const VOLUME_PADRAO = 50;
/** Ganho máximo da saída: os sons são baixinhos mesmo no volume 100. */
const GANHO_MAXIMO = 0.5;
/** Intervalo do tique do teclado: no máximo um clique por tique. */
export const INTERVALO_TECLADO_MS = 120;
/** Espaço mínimo entre dois sons da mesma categoria (o sino não repete a cada aviso). */
// o teclado fica um pouco abaixo do tique: a prévia ao ligar não pode furar a cadência
const ESPACO_MS: Record<Categoria, number> = { sino: 1500, teclado: INTERVALO_TECLADO_MS - 10, elevador: 600, concluida: 400, rodas: 300 };

// ------------------------------------------------------------------ regras puras

/** Lê as preferências gravadas. Texto inválido ou de outra versão cai no padrão, sem lançar. */
export function leituraPreferencias(bruto: string | null, legado = false): PreferenciasSons {
  const pref: PreferenciasSons = {
    volume: VOLUME_PADRAO,
    ligados: { sino: legado, teclado: false, elevador: false, concluida: false, rodas: false },
  };
  if (!bruto) return pref;
  try {
    const dados = JSON.parse(bruto) as { volume?: unknown; ligados?: Record<string, unknown> } | null;
    if (typeof dados?.volume === 'number' && Number.isFinite(dados.volume)) {
      pref.volume = Math.min(100, Math.max(0, Math.round(dados.volume)));
    }
    for (const cat of CATEGORIAS) {
      const valor = dados?.ligados?.[cat];
      if (typeof valor === 'boolean') pref.ligados[cat] = valor;
    }
  } catch {
    /* gravado de forma inválida: vale o padrão */
  }
  return pref;
}

/** Pode tocar agora? Volume zero, categoria desligada ou aba oculta (só o sino passa) impedem. */
export function podeTocar(pref: PreferenciasSons, cat: Categoria, oculta: boolean): boolean {
  if (pref.volume <= 0 || !pref.ligados[cat]) return false;
  return !oculta || cat === 'sino';
}

/**
 * Este tique do teclado tem clique? `digitando` é quantos agentes que trabalham estão na tela.
 * Mais gente, mais cliques, mas nunca dois no mesmo tique e no máximo metade dos tiques.
 * `sorteio` vem de Math.random().
 */
export function decideClique(digitando: number, sorteio: number): boolean {
  if (digitando <= 0) return false;
  return sorteio < Math.min(0.5, 0.2 + 0.05 * digitando);
}

/** Quantas pessoas chegaram e quantas saíram entre duas listas. Subagentes não contam (não usam o elevador). */
export function pessoasMudaram(
  antes: ReadonlyArray<Pick<Agent, 'id' | 'parentId'>>,
  depois: ReadonlyArray<Pick<Agent, 'id' | 'parentId'>>,
): { chegaram: number; sairam: number } {
  const estavam = new Set(antes.filter((a) => !a.parentId).map((a) => a.id));
  const estao = new Set(depois.filter((a) => !a.parentId).map((a) => a.id));
  let chegaram = 0;
  for (const id of estao) if (!estavam.has(id)) chegaram++;
  let sairam = 0;
  for (const id of estavam) if (!estao.has(id)) sairam++;
  return { chegaram, sairam };
}

// ------------------------------------------------------------------ estado e gravação

let pref: PreferenciasSons | undefined;

function lePreferencias(): PreferenciasSons {
  if (!pref) {
    let bruto: string | null = null;
    let legado = false;
    try {
      bruto = globalThis.localStorage?.getItem(CHAVE) ?? null;
      legado = bruto === null && globalThis.localStorage?.getItem(CHAVE_LEGADA) === '1';
    } catch {
      /* sem localStorage (modo privado): vale o padrão, nesta aba */
    }
    pref = leituraPreferencias(bruto, legado);
  }
  return pref;
}

function guardaPreferencias(): void {
  try {
    globalThis.localStorage?.setItem(CHAVE, JSON.stringify(lePreferencias()));
  } catch {
    /* sem persistência: vale só nesta aba */
  }
}

/** Há algum som ligado? O botão Som mostra o estado com essa resposta. */
export function sonsLigados(): boolean {
  const p = lePreferencias();
  return p.volume > 0 && CATEGORIAS.some((cat) => p.ligados[cat]);
}

// ------------------------------------------------------------------ áudio

interface Motor {
  ctx: AudioContext;
  saida: GainNode;
  ruido?: AudioBuffer;
}

let motor: Motor | undefined;

/** O contexto de áudio, criado na primeira vez (sempre depois de um clique da pessoa). */
function motorDeSom(): Motor | undefined {
  try {
    if (!motor) {
      const Contexto: typeof AudioContext | undefined =
        globalThis.AudioContext ?? (globalThis as { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
      if (!Contexto) return undefined;
      const ctx = new Contexto();
      const saida = ctx.createGain();
      saida.connect(ctx.destination);
      motor = { ctx, saida };
    }
    if (motor.ctx.state === 'suspended') void motor.ctx.resume();
    motor.saida.gain.value = (lePreferencias().volume / 100) * GANHO_MAXIMO;
    return motor;
  } catch {
    /* sem áudio neste navegador: segue calado */
    return undefined;
  }
}

/** Um tom com ataque curto e queda exponencial. Com `fim`, a frequência desliza até ele. */
function tom(m: Motor, freq: number, t0: number, dur: number, pico: number, tipo: OscillatorType = 'sine', fim = freq): void {
  const osc = m.ctx.createOscillator();
  const ganho = m.ctx.createGain();
  osc.type = tipo;
  osc.frequency.setValueAtTime(freq, t0);
  if (fim !== freq) osc.frequency.exponentialRampToValueAtTime(fim, t0 + dur);
  ganho.gain.setValueAtTime(0.0001, t0);
  ganho.gain.exponentialRampToValueAtTime(pico, t0 + 0.005);
  ganho.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  osc.connect(ganho).connect(m.saida);
  osc.start(t0);
  osc.stop(t0 + dur + 0.02);
}

/** Estalo: ruído curto passado por um filtro de faixa. É a base dos cliques e do estalo. */
function estalo(m: Motor, t0: number, dur: number, freq: number, pico: number): void {
  if (!m.ruido) {
    const buffer = m.ctx.createBuffer(1, Math.ceil(m.ctx.sampleRate * 0.05), m.ctx.sampleRate);
    const dados = buffer.getChannelData(0);
    for (let i = 0; i < dados.length; i++) dados[i] = Math.random() * 2 - 1;
    m.ruido = buffer;
  }
  const fonte = m.ctx.createBufferSource();
  fonte.buffer = m.ruido;
  const filtro = m.ctx.createBiquadFilter();
  filtro.type = 'bandpass';
  filtro.frequency.value = freq;
  filtro.Q.value = 1.2;
  const ganho = m.ctx.createGain();
  ganho.gain.setValueAtTime(pico, t0);
  ganho.gain.exponentialRampToValueAtTime(0.0001, t0 + dur);
  fonte.connect(filtro).connect(ganho).connect(m.saida);
  fonte.start(t0, 0, dur + 0.01);
}

// os sons: cada um recebe o motor e a hora de começar

/** Tique de teclado: um estalo bem baixinho, com a altura um pouco diferente a cada vez. */
function somClique(m: Motor, t0: number): void {
  estalo(m, t0, 0.012, 2800 + Math.random() * 900, 0.05);
}

/** Sino: um tom com parciais inarmônicas, como um sino de mesa de verdade. */
function somSino(m: Motor, t0: number): void {
  const parciais: Array<[number, number, number]> = [
    [1, 0.1, 1.6],
    [2, 0.045, 0.9],
    [2.76, 0.03, 0.6],
    [5.4, 0.012, 0.3],
  ];
  for (const [razao, pico, dur] of parciais) tom(m, 660 * razao, t0, dur, pico);
}

/** Ding do elevador: duas notas, uma depois da outra. */
function somElevador(m: Motor, t0: number): void {
  tom(m, 1318.5, t0, 0.9, 0.06);
  tom(m, 987.8, t0 + 0.28, 1.2, 0.05);
}

/** Estalo de tarefa concluída: um pop curto e um subidinha. */
function somConcluida(m: Motor, t0: number): void {
  estalo(m, t0, 0.03, 2600, 0.08);
  tom(m, 880, t0 + 0.02, 0.1, 0.05, 'triangle', 1320);
}

/** Pingue-pongue: quatro batidas alternadas, de madeira. */
function somPinguePongue(m: Motor, t0: number): void {
  for (let i = 0; i < 4; i++) {
    const t = t0 + i * 0.16;
    tom(m, i % 2 ? 1100 : 1400, t, 0.06, 0.07, 'triangle', i % 2 ? 770 : 980);
    estalo(m, t, 0.02, 2200, 0.02);
  }
}

/** Fliperama: três notas que sobem e um final, em onda quadrada. */
function somFliperama(m: Motor, t0: number): void {
  const notas: Array<[number, number]> = [
    [500, 1500],
    [700, 1800],
    [1000, 2400],
  ];
  notas.forEach(([de, ate], i) => tom(m, de, t0 + i * 0.11, 0.09, 0.03, 'square', ate));
  tom(m, 2400, t0 + 0.36, 0.14, 0.03, 'square', 1200);
}

/** Jokenpô: "jo-ken-pô", três batidas graves e um agudo no fim. */
function somJokenpo(m: Motor, t0: number): void {
  for (let i = 0; i < 3; i++) tom(m, 180, t0 + i * 0.22, 0.12, 0.1, 'triangle', 120);
  tom(m, 1200, t0 + 0.7, 0.18, 0.07, 'triangle', 1600);
}

const RODAS: Record<Roda, (m: Motor, t0: number) => void> = {
  'pingue-pongue': somPinguePongue,
  fliperama: somFliperama,
  jokenpo: somJokenpo,
};

const SOM_DA_CATEGORIA: Record<Categoria, (m: Motor, t0: number) => void> = {
  sino: somSino,
  teclado: somClique,
  elevador: somElevador,
  concluida: somConcluida,
  rodas: RODAS.fliperama,
};

const ultimoSom: Record<Categoria, number> = { sino: -Infinity, teclado: -Infinity, elevador: -Infinity, concluida: -Infinity, rodas: -Infinity };

/** Toca um som se a categoria pode agora e se já passou o espaço mínimo desde o último dela. */
function toca(cat: Categoria, som: (m: Motor, t0: number) => void): void {
  const oculta = globalThis.document?.hidden ?? false;
  if (!podeTocar(lePreferencias(), cat, oculta)) return;
  const agora = performance.now();
  if (agora - ultimoSom[cat] < ESPACO_MS[cat]) return;
  const m = motorDeSom();
  if (!m) return;
  ultimoSom[cat] = agora;
  som(m, m.ctx.currentTime + 0.01);
}

// ------------------------------------------------------------------ eventos

/** Alguém passou a precisar de você (o mesmo ponto em que sai o aviso). */
export function sino(): void {
  toca('sino', somSino);
}

/** Alguém chegou ou saiu do escritório: ding do elevador (um só, mesmo que sejam vários). */
export function elenco(antes: ReadonlyArray<Pick<Agent, 'id' | 'parentId'>>, depois: ReadonlyArray<Pick<Agent, 'id' | 'parentId'>>): void {
  const { chegaram, sairam } = pessoasMudaram(antes, depois);
  if (chegaram + sairam > 0) toca('elevador', somElevador);
}

/** Uma tarefa concluída (atividade 'done'). */
export function concluida(): void {
  toca('concluida', somConcluida);
}

/** Uma roda social (pingue-pongue, fliperama, jokenpô), para a vida social chamar. */
export function tocaRoda(tipo: Roda): void {
  toca('rodas', RODAS[tipo]);
}

/**
 * O tique do teclado: a cada INTERVALO_TECLADO_MS, se alguém que trabalha está na tela, talvez um clique.
 * `digitando` diz quantos agentes trabalhando estão visíveis agora (o campus responde).
 */
let relogio: ReturnType<typeof setInterval> | undefined;
let digitando: () => number = () => 0;

export function montaSons(contaDigitando: () => number): void {
  digitando = contaDigitando;
  if (relogio !== undefined) return;
  relogio = setInterval(() => {
    const oculta = globalThis.document?.hidden ?? false;
    if (!podeTocar(lePreferencias(), 'teclado', oculta)) return;
    if (decideClique(digitando(), Math.random())) toca('teclado', somClique);
  }, INTERVALO_TECLADO_MS);
}

// ------------------------------------------------------------------ painel

export interface PainelSons {
  el: HTMLElement;
  /** Relê as preferências gravadas (quando o painel abre). */
  atualiza(): void;
}

const ROTULO: Record<Categoria, Chave> = {
  sino: 'sons.sino',
  teclado: 'sons.teclado',
  elevador: 'sons.elevador',
  concluida: 'sons.concluida',
  rodas: 'sons.rodas',
};

export interface OpcoesPainelSons {
  /** Roda depois de cada mudança (o botão Som usa para mostrar o estado). */
  aoMudar?: () => void;
  /** Com ele, o painel ganha o botão Fechar (o desktop). No celular a folha já tem o ✕. */
  aoFechar?: () => void;
}

/**
 * O painel de sons: volume geral e uma caixa por categoria. Ligar uma categoria toca uma amostra.
 */
export function painelSons(opcoes: OpcoesPainelSons = {}): PainelSons {
  const el = document.createElement('section');
  el.className = 'sons-painel';
  el.setAttribute('role', 'group');
  el.setAttribute('aria-label', t('sons.titulo'));
  el.innerHTML = `<div class="sons-cab"><b>${t('sons.titulo')}</b><button type="button" class="sons-fechar">${t('sons.fechar')}</button></div>
    <p class="sons-dica">${t('sons.dica')}</p>
    <label class="sons-volume"><span>${t('sons.volume')}</span><input type="range" min="0" max="100" step="5" /><output></output></label>
    <div class="sons-lista">${CATEGORIAS.map((cat) => `<label class="sons-item"><input type="checkbox" data-cat="${cat}" /><span>${t(ROTULO[cat])}</span></label>`).join('')}</div>`;

  const fechar = el.querySelector<HTMLButtonElement>('.sons-fechar') as HTMLButtonElement;
  const volume = el.querySelector<HTMLInputElement>('.sons-volume input') as HTMLInputElement;
  const saida = el.querySelector<HTMLOutputElement>('.sons-volume output') as HTMLOutputElement;
  const caixas = [...el.querySelectorAll<HTMLInputElement>('.sons-item input')];
  const aoMudar = opcoes.aoMudar ?? (() => undefined);

  if (opcoes.aoFechar) fechar.addEventListener('click', opcoes.aoFechar);
  else fechar.hidden = true;

  function atualiza(): void {
    const p = lePreferencias();
    volume.value = String(p.volume);
    saida.textContent = `${p.volume}%`;
    for (const caixa of caixas) caixa.checked = p.ligados[caixa.dataset.cat as Categoria];
  }

  volume.addEventListener('input', () => {
    lePreferencias().volume = Number(volume.value);
    saida.textContent = `${volume.value}%`;
    guardaPreferencias();
    aoMudar();
  });
  // ao soltar o controle, um sino de amostra no volume escolhido (se o sino estiver ligado)
  volume.addEventListener('change', () => toca('sino', somSino));

  for (const caixa of caixas) {
    caixa.addEventListener('change', () => {
      const cat = caixa.dataset.cat as Categoria;
      lePreferencias().ligados[cat] = caixa.checked;
      guardaPreferencias();
      aoMudar();
      // ligar uma categoria toca a amostra dela (o clique da pessoa também libera o áudio)
      if (caixa.checked) toca(cat, SOM_DA_CATEGORIA[cat]);
    });
  }

  atualiza();
  return { el, atualiza };
}
